/**
 * ENG-6795: deterministic meta-text-egress detector.
 *
 * Runs host-side inside the conversation-eval pass (conversation-evaluator.ts),
 * over the reconstructed *agent* turns of a scored conversation, and flags when
 * internal/meta narration reached the end-user. Three classes, from the
 * ENG-6786 repros:
 *
 *   - repost_preamble  - a re-post preamble ("... didn't parse - posting it now")
 *                        that slipped the ENG-6794 egress sanitizer.
 *   - status_narration - the agent's third-person self-status going out as a
 *                        reply ("Assessment delivered to Samuel ... on his go").
 *   - loop_tick        - internal kanban housekeeping text ("Standing down - no
 *                        change", "All clear, no pending work").
 *
 * Design:
 *  - DETERMINISTIC, not an LLM judgment. Exact, free, regression-testable, and
 *    orthogonal to the Haiku "did the agent help the user" score.
 *  - This is the *observability* twin of the mcp egress sanitizer
 *    (packages/mcp/src/strip-repost-preamble.ts). The two are intentionally NOT
 *    a shared module: the sanitizer must be leading-only + fail-safe because it
 *    MUTATES outgoing text; this detector only FLAGS a conversation for human
 *    review. Each side is locked by its own tests.
 *  - Patterns are anchored on the leak's own phrasing, not on words that also
 *    occur in ordinary replies. A flag is only useful if it is rare, so bare
 *    "standing down", "no pending work", "delivered to", or a re-send plus
 *    "earlier" are deliberately NOT enough. The test file carries ordinary
 *    sentences that an earlier, broader version flagged (7 of 11), as negatives,
 *    and one positive per pattern that matches only that pattern.
 *  - PRIVACY: callers must emit only the boolean + kind labels to the control
 *    plane, never the matched text.
 */

export type MetaLeakKind = 'repost_preamble' | 'status_narration' | 'loop_tick';

/** All known kinds, for caller-side validation/filtering. */
export const META_LEAK_KINDS: readonly MetaLeakKind[] = [
  'repost_preamble',
  'status_narration',
  'loop_tick',
];

export interface MetaEgressResult {
  meta_leak: boolean;
  /** Sorted, de-duplicated list of the kinds detected across all messages. */
  meta_leak_kinds: MetaLeakKind[];
}

// --- repost_preamble ---------------------------------------------------------
// The leak refers to THE MESSAGE ITSELF ("posting it now", "this didn't parse").
// A legitimate reply names the thing being re-sent ("resending the link"), so
// every cue here requires a pronoun standing in for the message.

/** Only this many leading characters are searched: a preamble opens a message. */
export const REPOST_PREAMBLE_WINDOW = 200;

// A re-post announcement about the message itself.
const REPOST_ANNOUNCE =
  /\b(?:re-?(?:post|send)(?:ing)?\s+(?:it|this|that)\b|(?:posting|sending)\s+(?:it|this|that)\s+(?:now|again)\b|re-?posting\s+now\b)/i;
// Evidence the PRIOR attempt at this message failed to land: "it didn't parse",
// "couldn't send that".
const REPOST_FAILURE =
  /\b(?:it|this|that)\s+(?:did(?:n['’]t| not)|could(?:n['’]t| not)|wasn['’]t|failed to)\s+(?:parse|send|post|go through|land|deliver|come through)\b|\b(?:did(?:n['’]t| not)|could(?:n['’]t| not)|failed to)\s+(?:send|post|deliver)\s+(?:it|this|that)\b/i;
// ...or an explicit earlier attempt at this message: "I drafted this a moment ago".
const REPOST_PRIOR =
  /\b(?:drafted|dialled|dialed|typed|composed|wrote)\s+(?:this|that|it)\s+(?:a moment ago|moments ago|just now|earlier)\b/i;

/**
 * A delivered message carries a re-post preamble when, within its leading
 * window, it both announces a re-post AND references a failed or earlier
 * attempt at the same message.
 */
function hasRepostPreamble(message: string): boolean {
  const head = message.slice(0, REPOST_PREAMBLE_WINDOW);
  return REPOST_ANNOUNCE.test(head) && (REPOST_FAILURE.test(head) || REPOST_PRIOR.test(head));
}

// --- status_narration --------------------------------------------------------
// Third-person self-status that reads as an internal trace line, not a reply.
const STATUS_NARRATION: RegExp[] = [
  // "Assessment delivered to Samuel:" - a ledger line: the noun OPENS a sentence
  // (or parenthetical) and the recipient is a capitalised name. Case-sensitive
  // on purpose, so "Summary delivered to the board" and "Your answer delivered
  // to the customer" are not matched. \p{Lu} (not [A-Z]) so a non-ASCII
  // recipient ("Élodie") is still a capitalised name.
  /(?:^|[.!?]\s+|\(\s*)(?:[Aa]ssessment|[Ss]ummary|[Rr]eport|[Aa]nalysis|[Rr]esponse|[Rr]eply|[Uu]pdate|[Aa]nswer)\s+delivered\s+to\s+\p{Lu}/u,
  // third-person "on his/her/their go" (waiting on the user's go-ahead, narrated)
  /\bon (?:his|her|their) go\b(?![-\w])/i,
];

// --- loop_tick ---------------------------------------------------------------
// Canonical kanban-housekeeping lines. Each is the whole housekeeping phrase,
// never one of its words on its own.
const LOOP_TICK: RegExp[] = [
  // "Standing down - no change." / "Standing down, no action needed"
  /\bstanding down\s*[—–,:;.-]?\s*no (?:change|changes|action needed|new work)\b/i,
  // A message that is ONLY "Standing down."
  /^standing down[.!]?$/i,
  // "All clear, no pending work."
  /\ball clear\s*[,—–:;-]?\s*no pending work\b/i,
];

/**
 * Inspect the agent's delivered messages and report whether any meta-text leak
 * is present, with the de-duplicated set of kinds. Empty / falsy messages are
 * skipped. Never throws.
 */
export function detectMetaEgress(agentMessages: readonly string[]): MetaEgressResult {
  const kinds = new Set<MetaLeakKind>();
  for (const raw of agentMessages) {
    if (!raw) continue;
    const m = raw.trim();
    if (!m) continue;
    if (hasRepostPreamble(m)) kinds.add('repost_preamble');
    if (STATUS_NARRATION.some((re) => re.test(m))) kinds.add('status_narration');
    if (LOOP_TICK.some((re) => re.test(m))) kinds.add('loop_tick');
  }
  return {
    meta_leak: kinds.size > 0,
    meta_leak_kinds: [...kinds].sort(),
  };
}
